<template>
  <div class="panel panel-default panel-dashboard">
    <div class="panel-heading">
      <div class="row">
        <div class="col-xs-15">
          <h5>
            Sales Development
          </h5>
        </div>

        <div class="col-xs-4">
          <select-widget
              v-model="section"
              class="form-control input-sm"
              :nullable="true"
              placeholder="(all)"
              :options="sections"
          ></select-widget>
        </div>

        <div class="col-xs-5 text-right">
          <button type="button" class="btn btn-black btn-xs" @click="downloadBreakdown"
                  style="margin-top: 5px">
            <i class="fa fa-download"></i> Download
          </button>

          <button type="button" class="btn btn-black btn-xs" @click="expanded = !expanded"
                  style="margin-top: 5px">
            <i class="fa fa-search"></i> {{ expanded ? 'Collapse' : 'Expand' }}
          </button>
        </div>
      </div>
    </div>

    <loading-spinner v-if="loading" :style="loadingSpinnerStyle"></loading-spinner>

    <div v-else class="panel-body">
      <monthly-chart :metric-type="metricType" :section="section" :rows="rows" :filters="filters"
                     :loading="loading"></monthly-chart>

      <hr v-if="expanded">

      <monthly-table :metric-type="metricType" v-if="expanded" :section="section" :rows="rows"></monthly-table>
    </div>
  </div>
</template>

<script>
import http from '@/http'
import moment from 'moment'
import SaveHeight from '@/mixins/SaveHeight'
import LoadingSpinner from '@/components/Layout/LoadingSpinner'
import MonthlyChart from '@/pages/Dashboards/Subscriptions/Panels/SalesDevelopment/MonthlyChart'
import MonthlyTable from '@/pages/Dashboards/Subscriptions/Panels/SalesDevelopment/MonthlyTable'
import SelectWidget from '@/vendor/forms/FormGroups/Widgets/Select'
import axios from "axios";
import download from "downloadjs";
import store from "@/store";

export default {
  components: {LoadingSpinner, MonthlyChart, MonthlyTable, SelectWidget},

  mixins: [SaveHeight],

  props: ['year', 'metricType', 'portal', 'user', 'subscriberType'],

  data() {
    return {
      section: null,
      loading: true,
      expanded: localStorage.getItem('dashboards/subscriptions/sales-development/expanded') === 'true',
      rows: null,
      shared: store
    }
  },

  mounted() {
    this.load()
  },

  watch: {
    metricType() {
      this.load()
    },

    year() {
      this.load()
    },

    portal() {
      this.load()
    },

    user() {
      this.load()
    },

    subscriberType() {
      this.load()
    },

    expanded(value) {
      localStorage.setItem('dashboards/subscriptions/sales-development/expanded', value)
    },

    section() {
      this.load()
    }
  },

  computed: {
    start() {
      return moment().year(this.year).startOf('year').format('YYYY-MM-DD')
    },

    end() {
      return moment().year(this.year).endOf('year').format('YYYY-MM-DD')
    },

    filters() {
      let filters = []

      if (this.portal && this.portal.length) {
        filters.push({dimension: 'portal_id', operator: 'in', value: this.portal})
      }

      if (this.user) {
        filters.push({dimension: 'user_id', value: this.user})
      }

      if (this.subscriberType) {
        filters.push({dimension: 'subscriber_type', value: this.subscriberType})
      }

      return filters
    },

    months() {
      let startDate = moment().year(this.year).startOf('year')

      let endDate = moment().year(this.year).endOf('year')

      let result = []

      while (startDate.isBefore(endDate)) {
        result.push(startDate.clone())

        startDate.add(1, 'month')
      }

      return result
    },

    sections() {
      return [
        {value: 'new', label: 'New'},
        {value: 'renewal', label: 'Renewal'}
      ]
    }
  },

  methods: {
    load() {
      this.saveHeight()

      this.loading = true

      Promise
          .all([
            this.getTargetPromise(),
            this.getActualNewRowsPromise(),
            this.getExpectedNewRowsPromise(),
            this.getRenewedRowsPromise(),
            this.getEndingWithRenewalRowsPromise(),
          ])
          .then(responses => {
            responses[0].data.data = responses[0].data.data.map(row => {
              row.month = row.sold_month.substring(3,7)  + '-' + row.sold_month.substring(0,2)

              return row
            })

            this.rows = this.months.map(month => this.getRow(responses, month))

            this.loading = false
          })
    },

    getTargetPromise() {
      let filters = [
        {dimension: 'sold_year', operator: '=', value: this.year}
      ]

      if (this.portal && this.portal.length) {
        filters.push({dimension: 'portal_id', operator: 'in', value: this.portal})
      }

      return http.post('/api/v1/reports/new-analytics', {
        metrics: [{name: 'subscription_new_target'}, {name: 'subscription_renewal_target'}],
        filters: filters,
        dimensions: [{name: 'sold_month'}],
      })
    },

    getActualNewRowsPromise() {
      return http.post('/api/v1/reports/dashboard', {
        start: this.start,
        end: this.end,
        metrics: ['actual_starting_subscription_' + this.metricType],
        dimensions: ['month'],
        filters: this.filters.concat([
          {dimension: 'is_first_subscription_period', value: true}
        ])
      })
    },

    getExpectedNewRowsPromise() {
      return http.post('/api/v1/reports/dashboard', {
        start: this.start,
        end: this.end,
        metrics: ['expected_starting_subscription_' + this.metricType],
        dimensions: ['month'],
        filters: this.filters.concat([
          {dimension: 'is_first_subscription_period', value: true}
        ])
      })
    },

    getRenewedRowsPromise() {
      return http.post('/api/v1/reports/dashboard', {
        start: this.start,
        end: this.end,
        metrics: ['starting_subscription_' + this.metricType],
        dimensions: ['month'],
        filters: this.filters.concat([
          {dimension: 'is_first_subscription_period', value: false}
        ])
      })
    },

    getEndingWithRenewalRowsPromise() {
      let filters = this.filters
          .filter(filter => filter.dimension !== 'user_id')
          .concat([
            {dimension: 'has_automatic_renewal', value: true},
            {dimension: 'is_last_subscription_period', value: true},
            {dimension: 'subscription_end_date', operator: '>=', value: this.start + ' 00:00:00'},
            {dimension: 'subscription_end_date', operator: '<=', value: this.end + ' 23:59:59'}
          ])

      if (this.user) {
        filters.push({dimension: 'subscription_user_id', value: this.user})
      }

      return http.post('/api/v1/reports/new-dashboard', {
        metrics: [this.metricType === 'count' ? 'subscription_count' : 'subscription_renewal_revenue'],
        dimensions: ['subscription_end_month'],
        filters: filters
      })
    },

    getRow(responses, month) {
      let row = {
        month: month,
        new_target: this.metricType === 'revenue' ? this.getValueFromRow(responses[0], month, 'subscription_new_target') : 0,
        renewal_target: this.metricType === 'revenue' ? this.getValueFromRow(responses[0], month, 'subscription_renewal_target') : 0,
        target: this.metricType === 'revenue' ? this.getValueFromRow(responses[0], month, 'subscription_new_target') + this.getValueFromRow(responses[0], month, 'subscription_renewal_target') : 0,
        actual_new: this.getValueFromRow(responses[1], month, 'actual_starting_subscription_' + this.metricType),
        expected_new: this.getValueFromRow(responses[2], month, 'expected_starting_subscription_' + this.metricType),
        renewed: this.getValueFromRow(responses[3], month, 'starting_subscription_' + this.metricType),
        ending_with_renewal: this.getValueFromRow(responses[4], month, this.metricType === 'count' ? 'subscription_count' : 'subscription_renewal_revenue')
      }

      row.new = row.actual_new + row.expected_new

      row.value = row.actual_new + row.expected_new + row.renewed + row.ending_with_renewal

      row.total_renewal = row.renewed + row.ending_with_renewal

      return row
    },

    getValueFromRow(rows, month, value) {
      let row = rows.data.data.find(row => row.month === month.format('YYYY-MM') || row.subscription_end_month === month.format('YYYY-MM'))

      return row ? row[value] : 0
    },

    downloadBreakdown() {
      axios.post(
          '/api/v1/reports/subscription-sales-breakdown',
          {
            from: this.year + '-01-01',
            to: this.year + '-12-31',
            portal_id: this.portal,
            user_id: this.user,
            subscriber_type: this.subscriberType
          },
          {
            headers: {
              'Authorization': 'Bearer ' + this.shared.token
            },
            responseType: 'blob'
          }
      )
          .then(response => {
            download(response.data, response.headers['content-disposition'].slice(22, -1))
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
